/**
 * Copyright (c) Qwoach LLC 2026. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import {uuidv4} from "@/utils/helpers";
import {GenogramAnalyticsEventName} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramAnalyticsConstants";
import {
	createGenogramAnalyticsId,
	createGenogramAnalyticsSnapshot,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramAnalyticsHelper";
import {GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} from "@/core/js/model/SharedConstants";
import MeaningfulUsageTracker from "@/core/modules/review/MeaningfulUsageTracker";
import {
	GENOGRAM_MEANINGFUL_USAGE_CUMULATIVE_MS,
	GENOGRAM_MEANINGFUL_USAGE_IDLE_GAP_MS,
	GENOGRAM_MEANINGFUL_USAGE_STREAK_MS,
} from "@/tools/GenogramTool/review/GenogramReviewConstants";

const DAY_MS = 24 * 60 * 60 * 1000
const STORAGE_KEY_PREFIX = "qw_genogram_signal"

export default class GenogramAnalyticsSignalManager {

	constructor(analyticsManager) {
		this.analyticsManager = analyticsManager
		this.meaningfulUsageTracker = new MeaningfulUsageTracker({
			streakThresholdMs: GENOGRAM_MEANINGFUL_USAGE_STREAK_MS,
			cumulativeThresholdMs: GENOGRAM_MEANINGFUL_USAGE_CUMULATIVE_MS,
			idleGapMs: GENOGRAM_MEANINGFUL_USAGE_IDLE_GAP_MS,
		})
		this.resetWorkPeriod()
	}

	setAnalyticsManager(analyticsManager) {
		this.analyticsManager = analyticsManager
	}

	resetWorkPeriod() {
		this.sessionId = uuidv4()
		this.startedAtMs = Date.now()
		this.editCount = 0
		this.meaningfulUsageTracker.reset()
		this.hasTrackedToolOpened = false
		this.hasTrackedSessionStarted = false
		this.hasTrackedMeaningfulEditing = false
		this.baseContext = {}
	}

	startWorkPeriod(baseContext = {}) {
		this.baseContext = {
			...this.baseContext,
			...sanitizeProps(baseContext),
		}
		return this.sessionId
	}

	trackToolOpened(props = {}) {
		if(this.hasTrackedToolOpened){
			return
		}

		this.hasTrackedToolOpened = true
		this.trackEvent(GenogramAnalyticsEventName.TOOL_OPENED, props)
	}

	trackSessionStarted(props = {}) {
		if(this.hasTrackedSessionStarted){
			return
		}

		this.hasTrackedSessionStarted = true
		let eventProps = this.buildEventProps(props)
		this.trackEvent(GenogramAnalyticsEventName.SESSION_STARTED, eventProps)
		this.trackRevisitIfNeeded(eventProps)
		this.persistLastOpenedAt(eventProps.genogram_id, Date.now())
	}

	reportEditingActivity(props = {}) {
		this.editCount++
		let meaningfulUsageSignal = this.reportMeaningfulUsageActivity(props)
		let finalSignal = null
		if(meaningfulUsageSignal){
			finalSignal = this.buildMeaningfulEditingSignal(props, meaningfulUsageSignal)
			this.logDiagnostic("meaningful usage signal emitted", finalSignal)
		}
		return finalSignal
	}

	trackSaved(props = {}) {
		let eventProps = this.buildEventProps(props)
		let firstSaveAtMs = this.getFirstSavedAt(eventProps.genogram_id)
		let isFirstSave = props.isFirstSave
		if(isFirstSave == null){
			isFirstSave = firstSaveAtMs == null
		}

		eventProps.is_first_save = isFirstSave
		if(isFirstSave){
			firstSaveAtMs = Date.now()
			this.persistFirstSavedAt(eventProps.genogram_id, firstSaveAtMs)
		}
		eventProps.days_since_first_save = this.calculateDaysSince(firstSaveAtMs)
		this.trackEvent(GenogramAnalyticsEventName.SAVED, eventProps)
	}

	trackExported(props = {}) {
		let eventProps = this.buildEventProps(props)
		eventProps.days_since_first_save = this.calculateDaysSince(
			this.getFirstSavedAt(eventProps.genogram_id)
		)
		this.trackEvent(GenogramAnalyticsEventName.EXPORTED, eventProps)
	}

	trackContextPromptCompleted(props = {}) {
		let eventProps = this.buildEventProps(props)
		this.trackEvent(GenogramAnalyticsEventName.CONTEXT_PROMPT_COMPLETED, eventProps)
	}

	buildEventProps(props = {}) {
		let genogramId = createGenogramAnalyticsId(props.genogramItem, props.genogramId)
		let eventProps = {
			session_id: this.sessionId,
			genogram_id: genogramId,
			session_edit_count: this.editCount,
			session_duration_sec: this.getSessionDurationSec(),
			...this.baseContext,
			...createGenogramAnalyticsSnapshot(props.genogramItem),
			...sanitizeProps(props),
		}

		eventProps.days_since_last_open = this.calculateDaysSince(this.getLastOpenedAt(genogramId))
		delete eventProps.genogramItem
		delete eventProps.genogramId
		return sanitizeProps(eventProps)
	}

	trackRevisitIfNeeded(eventProps = {}) {
		let isExistingGenogram = eventProps.is_existing_genogram === true
		let daysSinceLastOpen = eventProps.days_since_last_open
		if(!isExistingGenogram || daysSinceLastOpen == null){
			return
		}

		let revisitProps = {
			...eventProps,
			days_since_first_save: this.calculateDaysSince(
				this.getFirstSavedAt(eventProps.genogram_id)
			),
		}
		this.trackEvent(GenogramAnalyticsEventName.REVISITED, revisitProps)
	}

	reportMeaningfulUsageActivity(props = {}) {
		let genogramId = createGenogramAnalyticsId(props.genogramItem, props.genogramId)
		let meaningfulUsageSignal = this.meaningfulUsageTracker.reportActivity(genogramId, Date.now())
		if(meaningfulUsageSignal && !this.hasTrackedMeaningfulEditing){
			this.hasTrackedMeaningfulEditing = true
			this.trackEvent(
				GenogramAnalyticsEventName.MEANINGFUL_EDITING_DETECTED,
				this.buildEventProps(props)
			)
			this.logDiagnostic("analytics meaningful editing tracked", {
				genogramId,
				meaningfulUsageSignal,
			})
		}
		return meaningfulUsageSignal
	}

	buildMeaningfulEditingSignal(props = {}, meaningfulUsageSignal = {}) {
		let eventProps = this.buildEventProps({
			...props,
			meaningful_editing_detected_at: meaningfulUsageSignal.detectedAt,
			meaningful_usage_metric: meaningfulUsageSignal.metric,
		})
		return {
			...eventProps,
			itemId: meaningfulUsageSignal.itemId,
			meaningful_editing_detected_at: meaningfulUsageSignal.detectedAt,
			meaningful_usage_metric: meaningfulUsageSignal.metric,
			meaningful_usage_streak_ms: meaningfulUsageSignal.streakDurationMs,
			meaningful_usage_cumulative_ms: meaningfulUsageSignal.cumulativeActiveMs,
		}
	}

	getSessionDurationSec() {
		return Math.max(0, Math.round((Date.now() - this.startedAtMs) / 1000))
	}

	getLastOpenedAt(genogramId) {
		return this.getStorageNumber(this.buildStorageKey(genogramId, "last_opened_at"))
	}

	persistLastOpenedAt(genogramId, timestampMs) {
		this.setStorageNumber(this.buildStorageKey(genogramId, "last_opened_at"), timestampMs)
	}

	getFirstSavedAt(genogramId) {
		return this.getStorageNumber(this.buildStorageKey(genogramId, "first_saved_at"))
	}

	persistFirstSavedAt(genogramId, timestampMs) {
		this.setStorageNumber(this.buildStorageKey(genogramId, "first_saved_at"), timestampMs)
	}

	buildStorageKey(genogramId, suffix) {
		return genogramId ? `${STORAGE_KEY_PREFIX}:${suffix}:${genogramId}` : null
	}

	getStorageNumber(storageKey) {
		if(!storageKey || typeof window === "undefined" || !window.localStorage){
			return null
		}

		let value = window.localStorage.getItem(storageKey)
		let nextValue = Number(value)
		return Number.isFinite(nextValue) && nextValue > 0 ? nextValue : null
	}

	setStorageNumber(storageKey, value) {
		if(!storageKey || typeof window === "undefined" || !window.localStorage){
			return
		}

		window.localStorage.setItem(storageKey, String(value))
	}

	calculateDaysSince(timestampMs) {
		let daysSince = null
		if(timestampMs != null){
			daysSince = Math.max(0, Math.floor((Date.now() - timestampMs) / DAY_MS))
		}
		return daysSince
	}

	trackEvent(eventName, props = {}) {
		this.analyticsManager?.trackEvent?.(eventName, sanitizeProps(props))
	}

	logDiagnostic(message, payload = {}) {
		console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramAnalyticsSignalManager: ${message}`, payload)
	}
}

function sanitizeProps(props = {}) {
	let nextProps = {}
	Object.keys(props || {}).forEach(key => {
		let value = props[key]
		if(value !== undefined && value !== null){
			nextProps[key] = value
		}
	})
	return nextProps
}
